import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@context/AuthContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import { useNotifications as useGlobalNotificationCount } from '@context/NotificationContext';
import { useLocation } from '@context/LocationContext';
import { useSocket } from './useSocket';
import api from '@utils/api';
import { getSocket } from '@utils/socket';
import { calculateDistance } from '@utils/helpers';
import { SOCKET_EVENTS } from '../../../shared/constants';

const requestPermission = async () => {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
};

/** Browser push for new activities within ~1 km of the user (Explore feed). */
export const useNearbyNotifications = () => {
  const { on } = useSocket();
  const { location } = useLocation();
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    requestPermission();
  }, []);

  useEffect(() => {
    const cleanup = on('activity_created', (activity) => {
      if (!activity?.location?.coordinates) return;
      const [lng, lat] = activity.location.coordinates;
      const loc = locationRef.current;
      if (loc?.lat == null || loc?.lng == null) return;
      const dist = calculateDistance(loc.lat, loc.lng, lat, lng);

      if (dist <= 1 && Notification.permission === 'granted') {
        new Notification('New vibe near you!', {
          body: `${activity.title} — Join now on Yaarvi`,
          icon: '/favicon.svg',
          tag: activity._id,
        });
      }
    });

    return cleanup;
  }, [on]);
};

function mergeNewMessage(prev, data) {
  const id = String(data.activityId);
  const t = data.time || new Date().toISOString();
  const row = {
    _id: id,
    activityId: id,
    senderName: data.senderName || 'Someone',
    senderAvatar: data.senderAvatar || '',
    activityName: data.activityName || 'Vibe',
    lastMessage: data.lastMessage || '',
    time: t,
    sortAt: t,
    isUnread: data.isUnread !== false,
  };
  const idx = prev.findIndex((c) => String(c.activityId) === id);
  const rest = idx === -1 ? prev : prev.filter((_, i) => i !== idx);
  return [row, ...rest];
}

/**
 * Inbox feed: conversations from `/api/messages/inbox`, ratings from notifications, Socket.IO `new_message`.
 * (Filename `useNotifications.js` per product spec; export is `useMessagesInbox` to avoid clashing with context `useNotifications`.)
 */
export function useMessagesInbox() {
  const { token, user } = useAuth();
  const { ready: publicConfigReady } = usePublicConfig();
  const { refreshUnreadCount } = useGlobalNotificationCount();
  const [conversations, setConversations] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [joins, setJoins] = useState([]);
  const [ratingReminders, setRatingReminders] = useState([]);
  const [activityReminders, setActivityReminders] = useState([]);
  const [photoFollowups, setPhotoFollowups] = useState([]);
  const [memoryShares, setMemoryShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingsLoading, setRatingsLoading] = useState(true);
  const [pulsingActivityId, setPulsingActivityId] = useState(null);
  const pulseTimer = useRef(null);

  const triggerPulse = useCallback((activityId) => {
    const id = String(activityId);
    clearTimeout(pulseTimer.current);
    setPulsingActivityId(id);
    pulseTimer.current = setTimeout(() => setPulsingActivityId(null), 600);
  }, []);

  const fetchAll = useCallback(async () => {
    if (!user || !token) {
      setConversations([]);
      setRatings([]);
      setJoins([]);
      setRatingReminders([]);
      setActivityReminders([]);
      setPhotoFollowups([]);
      setMemoryShares([]);
      setLoading(false);
      setRatingsLoading(false);
      return;
    }
    setLoading(true);
    setRatingsLoading(true);
    try {
      const [inboxRes, ratingsRes, joinsRes, remindersRes, ar1h, arDay, photoFu, memShares] = await Promise.all([
        api.get('/messages/inbox'),
        api.get('/notifications', { params: { type: 'rating', limit: 40, page: 1 } }),
        api.get('/notifications', { params: { type: 'join', limit: 40, page: 1 } }),
        api.get('/notifications', { params: { type: 'rating_reminder', limit: 40, page: 1 } }),
        api.get('/notifications', { params: { type: 'activity_reminder_1h', limit: 40, page: 1 } }),
        api.get('/notifications', { params: { type: 'activity_reminder_day', limit: 40, page: 1 } }),
        api.get('/notifications', { params: { type: 'activity_photo_followup', limit: 40, page: 1 } }),
        api.get('/notifications', { params: { type: 'memory_share_request', limit: 40, page: 1 } }),
      ]);
      setConversations(inboxRes.data.conversations || []);
      setRatings(ratingsRes.data.notifications || []);
      setJoins(joinsRes.data.notifications || []);
      setRatingReminders(remindersRes.data.notifications || []);
      const merged = [...(ar1h.data.notifications || []), ...(arDay.data.notifications || [])];
      merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setActivityReminders(merged);
      setPhotoFollowups(photoFu.data.notifications || []);
      setMemoryShares(memShares.data.notifications || []);
    } catch {
      setConversations([]);
      setRatings([]);
      setJoins([]);
      setRatingReminders([]);
      setActivityReminders([]);
      setPhotoFollowups([]);
      setMemoryShares([]);
    } finally {
      setLoading(false);
      setRatingsLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!user || !token || !publicConfigReady) return undefined;
    const socket = getSocket(token);
    if (!socket) return undefined;

    const onNewMessage = (data) => {
      if (!data?.activityId) return;
      setConversations((prev) => mergeNewMessage(prev, data));
      triggerPulse(data.activityId);
      refreshUnreadCount();
    };

    const onNewNotification = (payload) => {
      if (payload?.reason === 'rating') {
        api
          .get('/notifications', { params: { type: 'rating', limit: 40, page: 1 } })
          .then((res) => setRatings(res.data.notifications || []))
          .catch(() => {});
      }
      if (payload?.reason === 'join' || payload?.reason === 'leave') {
        api
          .get('/notifications', { params: { type: 'join', limit: 40, page: 1 } })
          .then((res) => setJoins(res.data.notifications || []))
          .catch(() => {});
      }
      if (payload?.reason === 'rating_reminder') {
        api
          .get('/notifications', { params: { type: 'rating_reminder', limit: 40, page: 1 } })
          .then((res) => setRatingReminders(res.data.notifications || []))
          .catch(() => {});
      }
      if (payload?.reason === 'activity_reminder') {
        Promise.all([
          api.get('/notifications', { params: { type: 'activity_reminder_1h', limit: 40, page: 1 } }),
          api.get('/notifications', { params: { type: 'activity_reminder_day', limit: 40, page: 1 } }),
        ])
          .then(([a, b]) => {
            const merged = [...(a.data.notifications || []), ...(b.data.notifications || [])];
            merged.sort((x, y) => new Date(y.createdAt).getTime() - new Date(x.createdAt).getTime());
            setActivityReminders(merged);
          })
          .catch(() => {});
      }
      if (payload?.reason === 'activity_photo_followup') {
        api
          .get('/notifications', { params: { type: 'activity_photo_followup', limit: 40, page: 1 } })
          .then((res) => setPhotoFollowups(res.data.notifications || []))
          .catch(() => {});
      }
      if (payload?.reason === 'memory_share_request') {
        api
          .get('/notifications', { params: { type: 'memory_share_request', limit: 40, page: 1 } })
          .then((res) => setMemoryShares(res.data.notifications || []))
          .catch(() => {});
      }
    };

    socket.on(SOCKET_EVENTS.NEW_MESSAGE, onNewMessage);
    socket.on(SOCKET_EVENTS.NEW_NOTIFICATION, onNewNotification);
    return () => {
      socket.off(SOCKET_EVENTS.NEW_MESSAGE, onNewMessage);
      socket.off(SOCKET_EVENTS.NEW_NOTIFICATION, onNewNotification);
      clearTimeout(pulseTimer.current);
    };
  }, [user, token, publicConfigReady, triggerPulse, refreshUnreadCount]);

  const markActivityRead = useCallback(
    async (activityId) => {
      const id = String(activityId);
      setConversations((prev) =>
        prev.map((c) => (String(c.activityId) === id ? { ...c, isUnread: false } : c))
      );
      try {
        await api.patch(`/messages/read/${id}`);
        await refreshUnreadCount();
      } catch {
        fetchAll();
      }
    },
    [fetchAll, refreshUnreadCount]
  );

  const notifRowId = (n) => String(n._id ?? n.id ?? '');

  const onRatingRead = useCallback(
    async (notificationId) => {
      const idStr = String(notificationId);
      setRatings((prev) =>
        prev.map((n) => (notifRowId(n) === idStr ? { ...n, isRead: true } : n))
      );
      await refreshUnreadCount();
    },
    [refreshUnreadCount]
  );

  const onJoinRead = useCallback(async (notificationId) => {
    const idStr = String(notificationId);
    setJoins((prev) =>
      prev.map((n) => (notifRowId(n) === idStr ? { ...n, isRead: true } : n))
    );
  }, []);

  const onRatingReminderRead = useCallback(
    async (notificationId) => {
      const idStr = String(notificationId);
      setRatingReminders((prev) =>
        prev.map((n) => (notifRowId(n) === idStr ? { ...n, isRead: true } : n))
      );
      await refreshUnreadCount();
    },
    [refreshUnreadCount]
  );

  const onActivityReminderRead = useCallback(
    async (notificationId) => {
      const idStr = String(notificationId);
      setActivityReminders((prev) =>
        prev.map((n) => (notifRowId(n) === idStr ? { ...n, isRead: true } : n))
      );
      await refreshUnreadCount();
    },
    [refreshUnreadCount]
  );

  const onPhotoFollowupRead = useCallback(
    async (notificationId) => {
      const idStr = String(notificationId);
      setPhotoFollowups((prev) =>
        prev.map((n) => (notifRowId(n) === idStr ? { ...n, isRead: true } : n))
      );
      await refreshUnreadCount();
    },
    [refreshUnreadCount]
  );

  const onMemoryShareHandled = useCallback(
    async (notificationId) => {
      const idStr = String(notificationId);
      setMemoryShares((prev) =>
        prev.map((n) => (notifRowId(n) === idStr ? { ...n, isRead: true } : n))
      );
      await refreshUnreadCount();
    },
    [refreshUnreadCount]
  );

  const sortedConversations = useMemo(() => {
    return [...conversations].sort(
      (a, b) =>
        new Date(b.sortAt || b.time).getTime() - new Date(a.sortAt || a.time).getTime()
    );
  }, [conversations]);

  return {
    conversations: sortedConversations,
    ratings,
    joins,
    ratingReminders,
    activityReminders,
    photoFollowups,
    memoryShares,
    loading,
    ratingsLoading,
    refresh: fetchAll,
    markActivityRead,
    onRatingRead,
    onJoinRead,
    onRatingReminderRead,
    onActivityReminderRead,
    onPhotoFollowupRead,
    onMemoryShareHandled,
    pulsingActivityId,
  };
}
